import { Reminder, ReminderBasedOn } from '@griply/types';
import {
  getNonPresetReminders,
  getReminderLabel,
  getReminderOptions,
  isSameReminder,
  toggleReminder,
} from '@griply/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellDelete, Check16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as PopupMenuColors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { InteractionType } from 'shared/types/data-type';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { v4 as uuidv4 } from 'uuid';

export type RemindersPopupMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  reminders?: Reminder[] | null;
  hasTime: boolean;
  timeFormat: TimeFormatOptions;
  onChange: (reminders: Reminder[]) => void;
  onClose: () => void;
};

export const RemindersPopupMenu: React.FC<RemindersPopupMenuProps> = ({
  reminders,
  hasTime,
  timeFormat,
  onChange,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();

  const activeReminders = reminders ?? [];
  const options = getReminderOptions(hasTime);
  const customReminders = getNonPresetReminders(activeReminders, options);

  const onToggle = (offset: number, basedOn: ReminderBasedOn) =>
    onChange(
      toggleReminder(activeReminders, { id: uuidv4(), offset, basedOn }),
    );

  const onClear = () => {
    onChange([]);
    onClose();
  };

  return (
    <PopupMenu
      interactionType={InteractionType.Task}
      {...rest}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder isScrollable>
        {options.map((option) => {
          const selected = activeReminders.some((reminder) =>
            isSameReminder(reminder, option),
          );
          const onItem = () => onToggle(option.offset, option.basedOn);

          return (
            <PopupMenuListItem key={option.labelKey}>
              <PopupMenuButton
                start={<Icon icon={Bell} />}
                end={selected ? <Icon icon={Check16} /> : undefined}
                isSelected={selected}
                onClick={onItem}
                tabIndex={0}
                type="button"
              >
                {t(option.labelKey)}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}

        {customReminders.map((reminder) => {
          const onItem = () => onToggle(reminder.offset, reminder.basedOn);
          const label =
            getReminderLabel(reminder, t) ??
            parseDateTime(reminder.offset, timeFormat);

          return (
            <PopupMenuListItem
              key={reminder.id ?? `${reminder.basedOn}-${reminder.offset}`}
            >
              <PopupMenuButton
                start={<Icon icon={Bell} />}
                end={<Icon icon={Check16} />}
                isSelected
                onClick={onItem}
                tabIndex={0}
                type="button"
              >
                {label}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>

      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={BellDelete} />}
            startColor={PopupMenuColors.Error}
            textColor={PopupMenuColors.Error}
            onClick={onClear}
            tabIndex={0}
            type="button"
          >
            {t('reminders.none')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
